import { useMemo, useState } from "react";
import { ButtonSpinner } from "../../../../components/atoms/Button/Button.styles";
import { ConfirmationModal } from "../../../../components/organisms/Modal/confirmationModal";
import { useNotification } from "../../../../hooks/useNotification";
import {
  useCancelPicklistMutation,
  useMarkReadyToPackPicklistMutation,
  useReleasePicklistMutation,
} from "../../../../store/apiSlice/warehouseApiSlice";
import { componentWithCondition } from "../../../newLayout/page/components/hoc/componentWithConditionCheck";
import { ListActionBurgerMenu } from "../../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";

const PicklistAction = ({ selectedOrderId, setSelectedOrder, selectedOrderFromListview }) => {
  const notify = useNotification();

  const [releasePicklist, { isLoading: isReleasing }] = useReleasePicklistMutation();
  const [cancelPicklist, { isLoading: isCancellingPicklist }] = useCancelPicklistMutation();
  const [markReadyToPackPicklist, { isLoading: isMarkingReady }] =
    useMarkReadyToPackPicklistMutation();

  const [isCancelling, setIsCancelling] = useState(false);

  const handleRelease = async () => {
    try {
      await releasePicklist({ picklist_ids: selectedOrderId }).unwrap();
      notify({
        type: "success",
        message:
          selectedOrderId.length === 1
            ? `Picklist “${selectedOrderFromListview[0]?.name}” released successfully.`
            : `${selectedOrderId.length} Picklists released successfully.`,
      });
      setSelectedOrder([]);
    } catch (error) {
      notify({
        type: "error",
        message:
          error.data?.detail ||
          `Failed to release picklist${selectedOrderId.length > 1 ? "(s)" : ""}.`,
      });
    }
  };

  const handleReadyToPack = async () => {
    try {
      await markReadyToPackPicklist({ picklist_ids: selectedOrderId }).unwrap();
      notify({
        type: "success",
        message: "Picklist(s) marked as ready to pack successfully.",
      });
      setSelectedOrder([]);
    } catch (error) {
      notify({ type: "error", message: error.data?.detail || "Failed to mark ready to pack." });
    }
  };

  const handleCancelSubmit = async () => {
    try {
      await cancelPicklist({ picklist_ids: selectedOrderId }).unwrap();
      notify({
        type: "success",
        message:
          selectedOrderId.length === 1
            ? `Picklist “${selectedOrderFromListview[0]?.name}” cancelled successfully.`
            : `${selectedOrderId.length} Picklists cancelled successfully.`,
      });
      setSelectedOrder([]);
      setIsCancelling(false);
    } catch (error) {
      notify({ type: "error", message: error.data?.detail || "Failed to cancel picklist." });
    }
  };

  const hasSelection = selectedOrderId.length > 0;

  const isReleaseEligible = useMemo(
    () => hasSelection && selectedOrderFromListview.every(order => order.status?.id === "created"),
    [hasSelection, selectedOrderFromListview],
  );

  const isCancelEligible = useMemo(
    () =>
      hasSelection &&
      selectedOrderFromListview.every(order => {
        const orderStatus = order.status?.id;
        return ["created", "released"].includes(orderStatus);
      }),
    [hasSelection, selectedOrderFromListview],
  );

  const isReadyToPackEligible = useMemo(() => {
    return (
      hasSelection &&
      selectedOrderFromListview.every(order => {
        const orderStatus = order.status?.id;
        return orderStatus === "fully_picked" || orderStatus === "partially_picked";
      })
    );
  }, [hasSelection, selectedOrderFromListview]);

  const burgerItems = useMemo(
    () => [
      {
        key: "cancel",
        label: "Cancel",
        onClick: () => setIsCancelling(true),
        disabled: !isCancelEligible || isCancellingPicklist,
        title: isCancelEligible ? "" : "Select picklists in 'created' or 'released' status.",
        isLoading: isCancellingPicklist,
        isDanger: true,
      },
    ],
    [isCancelEligible, isCancellingPicklist],
  );

  return (
    <>
      <li
        className={`tableButton ${isReleaseEligible && !isReleasing ? "activeState" : "disableState"}`}
      >
        <button
          onClick={handleRelease}
          disabled={!isReleaseEligible || isReleasing}
          title={isReleaseEligible ? "" : "Select picklists in 'created' status."}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isReleasing && <ButtonSpinner aria-hidden="true" />}
          {isReleasing ? "Releasing…" : "Release Picklist"}
        </button>
      </li>
      <li
        className={`tableButton ${isReadyToPackEligible && !isMarkingReady ? "activeState" : "disableState"}`}
      >
        <button
          onClick={handleReadyToPack}
          disabled={!isReadyToPackEligible || isMarkingReady}
          title={isReadyToPackEligible ? "" : "Select picklists in 'fully_picked' status."}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isMarkingReady && <ButtonSpinner aria-hidden="true" />}
          {isMarkingReady ? "Updating…" : "Ready to Pack"}
        </button>
      </li>

      <ListActionBurgerMenu items={burgerItems} />

      {isCancelling && (
        <ConfirmationModal
          titleOfModal="Cancel Picklist"
          btnName="Cancel Picklist"
          closeModal={() => {
            setIsCancelling(false);
          }}
          handleModalWork={handleCancelSubmit}
          isLoading={isCancellingPicklist}
          description={<>Are you sure you want to cancel the selected picklist(s)?</>}
        />
      )}
    </>
  );
};

export const PicklistActionWithCheck = componentWithCondition(PicklistAction);
